import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "../../../components/ui/Button";
import { AnimatedAskBar } from "../../../components/ui/AnimatedAskBar";
import { cn } from "../../../lib/utils";
import {
  TYPEWRITER_SPEED,
  useTypewriter,
} from "../../../domains/gpt/hooks/useTypewriter";

// Gap between two consecutive elements of the entrance cascade. Everything
// below the headline slides up in reading order once the headline has finished
// typing.
const STAGGER_STEP_MS = 60;

/**
 * First slide of the onboarding carousel: what Juisci is, what it can do, and
 * the healthcare-professional gate. Both CTAs advance to the name step; the
 * secondary one records that the user is not an HCP, which takes the France-only
 * RPPS lookup out of the flow further down.
 *
 * The slide introduces the agent by acting like it: the headline types itself
 * with the same `useTypewriter` + blinking mint caret as a JuisciGPT answer,
 * then the rest of the page fades up behind it.
 *
 * Chrome-free by design: it renders its own footer, so the layouts suppress the
 * shared header/footer bands here.
 */
export function IntroStep({
  onAccept,
  onDeclineHcp,
}: {
  onAccept: () => void;
  onDeclineHcp: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  // Literal t() calls per capability keep the i18n scanner able to extract the
  // keys; the index only drives the badge.
  const capabilities = [
    {
      title: t("Answer your clinical questions"),
      body: t(
        "I search peer-reviewed literature and return sourced, cited answers in seconds."
      ),
    },
    {
      title: t("Keep you up to date"),
      body: t(
        "I surface the latest studies and guidelines relevant to your specialty."
      ),
    },
    {
      title: t("Let you go deeper"),
      body: t("Read full papers, chat with pdf, verify sources on your terms."),
    },
  ];

  const headline = t("Hi, I'm Juisci.");
  const reveal = useTypewriter(headline, true, TYPEWRITER_SPEED.headline, 0);

  // Nothing below the headline exists visually until the headline is typed:
  // before that it is transparent but still laid out (so nothing reflows when
  // it appears), after that it runs the shared stagger, whose `backwards` fill
  // holds it at opacity 0 through its own delay.
  const revealClass = reveal.done ? "animate-stagger-in" : "opacity-0";

  // Per-position delay, so it must be inline — there is no class for "the 4th
  // element of the cascade".
  function revealDelay(position: number): React.CSSProperties {
    return { animationDelay: `${position * STAGGER_STEP_MS}ms` };
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-10">
      <div className="flex flex-col gap-3">
        {/* Both spans share one grid cell so the heading always reserves the
            height of the FULL headline — a locale whose title wraps would
            otherwise push the whole page down mid-reveal. */}
        <h1
          aria-label={headline}
          className="grid font-dm-sans text-heading-display font-bold text-primary-black"
        >
          <span aria-hidden className="invisible col-start-1 row-start-1">
            {headline}
          </span>
          <span
            aria-hidden
            className={cn(
              "col-start-1 row-start-1",
              reveal.done ? "" : "typing-caret"
            )}
          >
            {reveal.text}
          </span>
        </h1>
        <p
          style={revealDelay(0)}
          className={cn(
            "font-dm-sans text-heading-xs font-medium text-ds-slate-600",
            revealClass
          )}
        >
          {t("AI that thinks like a clinician, backed by evidence.")}
        </p>
      </div>

      <div className="flex flex-1 flex-col gap-6">
        <div style={revealDelay(1)} className={revealClass}>
          <AnimatedAskBar tone="on-surface" size="mobile" />
        </div>

        <h2
          style={revealDelay(2)}
          className={cn(
            "font-dm-sans text-publication-lg font-bold text-primary-black mt-4",
            revealClass
          )}
        >
          {t("Here's what I can do")}
        </h2>

        <ul className="flex list-none flex-col gap-5 p-0">
          {capabilities.map((capability, index) => (
            <li
              key={capability.title}
              style={revealDelay(3 + index)}
              className={cn("flex gap-4", revealClass)}
            >
              <span
                aria-hidden
                className="flex size-7 shrink-0 items-center justify-center rounded-xl bg-ds-moss-100 font-dm-sans text-ui-body font-semibold text-ds-moss-600"
              >
                {index + 1}
              </span>
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                <p className="font-dm-sans text-heading-xs font-semibold text-primary-black">
                  {capability.title}
                </p>
                <p className="font-dm-sans text-ui-body text-ds-slate-600">
                  {capability.body}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div
        style={revealDelay(6)}
        className={cn("flex flex-col gap-6", revealClass)}
      >
        <p className="text-caption text-center text-ds-slate-700">
          <Trans
            i18nKey="By continuing, you agree to the <termsLink>Terms of Service</termsLink> and <policyLink>Privacy Policy</policyLink>, and certify that you are a healthcare professional."
            components={{
              termsLink: (
                <Link
                  className="font-semibold text-ds-mint-800 underline"
                  to="/terms?isPush=true&hideTitle=true"
                />
              ),
              policyLink: (
                <Link
                  className="font-semibold text-ds-mint-800 underline"
                  to="/policy?isPush=true&hideTitle=true"
                />
              ),
            }}
          />
        </p>

        <div className="flex flex-col gap-2">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={onAccept}
          >
            {t("Accept & continue")}
          </Button>
          <Button
            variant="ghost"
            size="md"
            className="w-full"
            onClick={onDeclineHcp}
          >
            {t("I'm not a healthcare professional")}
          </Button>
        </div>
      </div>
    </div>
  );
}
